import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  FormGroup, FormControlLabel, Checkbox, Button, Alert, Grid, List, ListItem,
  ListItemText, ListItemIcon, Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  DragIndicator as DragIndicatorIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import api from '../api';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

const pageConfigs = {
  assets: {
    label: 'Assets Page',
    columns: [
      { id: 'asset_number', label: 'Asset Number', required: true },
      { id: 'asset_description', label: 'Description', required: true },
      { id: 'category', label: 'Category', required: false },
      { id: 'location', label: 'Location', required: false },
      { id: 'manufacturer', label: 'Manufacturer', required: false },
      { id: 'model', label: 'Model', required: false },
      { id: 'serial_number', label: 'Serial Number', required: false },
      { id: 'installation_date', label: 'Installation Date', required: false },
      { id: 'department', label: 'Department', required: false },
      { id: 'current_meter_reading', label: 'Meter Reading', required: false },
      { id: 'last_maintenance_date', label: 'Last Maintenance', required: false },
    ]
  },
  work_orders: {
    label: 'Work Orders Page',
    columns: [
      { id: 'work_order_number', label: 'Work Order #', required: true },
      { id: 'asset_number', label: 'Asset Number', required: true },
      { id: 'asset_description', label: 'Asset Name', required: false },
      { id: 'type', label: 'Type', required: false },
      { id: 'title', label: 'Title', required: true },
      { id: 'priority', label: 'Priority', required: false },
      { id: 'status', label: 'Status', required: true },
      { id: 'assigned_to_name', label: 'Assigned To', required: false },
      { id: 'created_at', label: 'Created', required: false },
      { id: 'completed_at', label: 'Completed', required: false },
    ]
  },
  pm_schedules: {
    label: 'PM Schedules Page',
    columns: [
      { id: 'asset_number', label: 'Asset Number', required: true },
      { id: 'asset_description', label: 'Asset Name', required: false },
      { id: 'title', label: 'Title', required: true },
      { id: 'schedule_type', label: 'Type', required: false },
      { id: 'interval_value', label: 'Interval', required: false },
      { id: 'next_due_date', label: 'Next Due', required: false },
      { id: 'priority', label: 'Priority', required: false },
      { id: 'is_active', label: 'Status', required: true },
    ]
  },
  users: {
    label: 'Users Page',
    columns: [
      { id: 'username', label: 'Username', required: true },
      { id: 'full_name', label: 'Full Name', required: true },
      { id: 'email', label: 'Email', required: true },
      { id: 'role', label: 'Role', required: true },
      { id: 'created_at', label: 'Created At', required: false },
    ]
  }
};

export default function ColumnSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const { tableRowHover, pmTypeText, accentBlue, gradientPrimary, gradientPrimaryHover } = useThemeColors();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/column-settings');

      // Convert array of settings to object keyed by page_name
      const settingsObj = {};
      response.data.forEach(setting => {
        settingsObj[setting.page_name] = JSON.parse(setting.visible_columns);
      });

      // Initialize with defaults for pages without settings
      Object.keys(pageConfigs).forEach(pageName => {
        if (!settingsObj[pageName]) {
          // Default: all columns visible in default order
          settingsObj[pageName] = pageConfigs[pageName].columns.map(col => col.id);
        }
      });

      setSettings(settingsObj);
      setLoading(false);
    } catch (err) {
      setError('Failed to load column settings');
      setLoading(false);
    }
  };

  const handleToggleColumn = (pageName, columnId) => {
    setSettings(prev => {
      const pageSettings = prev[pageName] || [];
      const isVisible = pageSettings.includes(columnId);

      // Check if this is a required column
      const column = pageConfigs[pageName].columns.find(col => col.id === columnId);
      if (column?.required) {
        // Don't allow unchecking required columns
        return prev;
      }

      if (isVisible) {
        // Remove from array
        return {
          ...prev,
          [pageName]: pageSettings.filter(id => id !== columnId)
        };
      } else {
        // Add to array in correct position based on default column order
        const defaultOrder = pageConfigs[pageName].columns.map(col => col.id);
        const newSettings = [...pageSettings, columnId];
        // Sort by default order
        newSettings.sort((a, b) => defaultOrder.indexOf(a) - defaultOrder.indexOf(b));
        return {
          ...prev,
          [pageName]: newSettings
        };
      }
    });
  };

  const handleDragStart = (pageName, columnId) => {
    setDraggedItem({ pageName, columnId });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (pageName, targetColumnId) => {
    if (!draggedItem || draggedItem.pageName !== pageName) return;

    setSettings(prev => {
      const pageSettings = [...(prev[pageName] || [])];
      const draggedIndex = pageSettings.indexOf(draggedItem.columnId);
      const targetIndex = pageSettings.indexOf(targetColumnId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      // Remove dragged item
      pageSettings.splice(draggedIndex, 1);
      // Insert at new position
      pageSettings.splice(targetIndex, 0, draggedItem.columnId);

      return {
        ...prev,
        [pageName]: pageSettings
      };
    });

    setDraggedItem(null);
  };

  const handleSaveSettings = async (pageName) => {
    try {
      await api.put(`/column-settings/${pageName}`, {
        visible_columns: settings[pageName]
      });
      setSuccess(`Settings for ${pageConfigs[pageName].label} saved successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleResetSettings = async (pageName) => {
    if (!window.confirm(`Reset ${pageConfigs[pageName].label} columns to default?`)) {
      return;
    }

    try {
      await api.delete(`/column-settings/${pageName}`);

      // Reset to all columns visible
      setSettings(prev => ({
        ...prev,
        [pageName]: pageConfigs[pageName].columns.map(col => col.id)
      }));

      setSuccess(`Settings for ${pageConfigs[pageName].label} reset to defaults`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset settings');
    }
  };

  const getOrderedColumns = (pageName) => {
    const pageSettings = settings[pageName] || [];
    // Return columns in the order they appear in settings
    return pageSettings.map(columnId => {
      const column = pageConfigs[pageName].columns.find(col => col.id === columnId);
      return column || { id: columnId, label: columnId, required: false };
    });
  };

  const getHiddenColumns = (pageName) => {
    const pageSettings = settings[pageName] || [];
    return pageConfigs[pageName].columns.filter(col => !pageSettings.includes(col.id));
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
        Column Settings
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize which columns are visible on each page and their order. Drag columns to reorder them.
        These settings will apply to all users. Required columns cannot be hidden.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {Object.keys(pageConfigs).map(pageName => (
        <Accordion key={pageName} defaultExpanded={false} sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{
              background: gradientPrimary,
              color: 'white',
              '& .MuiAccordionSummary-content': { my: 1.5 }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{pageConfigs[pageName].label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                  Visible Columns (drag to reorder):
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                  <List dense>
                    {getOrderedColumns(pageName).map((column) => (
                      <ListItem
                        key={column.id}
                        draggable
                        onDragStart={() => handleDragStart(pageName, column.id)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(pageName, column.id)}
                        sx={{
                          cursor: 'move',
                          bgcolor: 'background.paper',
                          mb: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          '&:hover': { bgcolor: tableRowHover }
                        }}
                      >
                        <ListItemIcon>
                          <DragIndicatorIcon sx={{ color: accentBlue }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 500 }}>{column.label}</Typography>
                              {column.required && (
                                <Chip
                                  label="Required"
                                  size="small"
                                  sx={{
                                    backgroundColor: tableRowHover,
                                    color: pmTypeText,
                                    border: '1px solid #2196f3',
                                    fontWeight: 500,
                                    height: 20,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                            </Box>
                          }
                        />
                        {!column.required && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={true}
                                onChange={() => handleToggleColumn(pageName, column.id)}
                                sx={{
                                  color: accentBlue,
                                  '&.Mui-checked': { color: accentBlue },
                                }}
                              />
                            }
                            label="Visible"
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {getHiddenColumns(pageName).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: accentBlue, fontWeight: 600 }}>
                    Hidden Columns:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                    <FormGroup>
                      {getHiddenColumns(pageName).map(column => (
                        <FormControlLabel
                          key={column.id}
                          control={
                            <Checkbox
                              checked={false}
                              onChange={() => handleToggleColumn(pageName, column.id)}
                              disabled={column.required}
                              sx={{
                                color: accentBlue,
                                '&.Mui-checked': { color: accentBlue },
                              }}
                            />
                          }
                          label={column.label}
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSaveSettings(pageName)}
                    sx={{
                      background: gradientPrimary,
                      borderRadius: 2,
                      textTransform: 'none',
                      '&:hover': {
                        background: gradientPrimaryHover,
                      },
                    }}
                  >
                    Save Settings
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ResetIcon />}
                    onClick={() => handleResetSettings(pageName)}
                    sx={{
                      borderColor: navyBlue,
                      color: accentBlue,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
